/**
 * Projects: the register, stages and gates, members, milestones, and the
 * location tree.
 */

import { request } from './client.js';

// --------------------------------------------------------------------- projects

export type ProjectType =
  | 'office'
  | 'retail'
  | 'f_and_b'
  | 'residential'
  | 'hospitality'
  | 'other';

export type StageStatus = 'not_started' | 'active' | 'at_risk' | 'blocked' | 'complete';

export interface ProjectStage {
  readonly stage_key: string;
  readonly status: string;
  readonly sequence: number;
  readonly is_overridden: boolean;
}

/**
 * What is holding a gate item open (ADR-033).
 *
 * EVERY FIELD IS OPTIONAL AND THAT IS THE CONTRACT, not a convenience. Three
 * separate things produce an absent field and the screen must read all three as
 * "say nothing" rather than "zero":
 *
 *   * the item is a yes/no fact with nothing to count — a start date, a client;
 *   * the item is satisfied, so nothing is holding it;
 *   * **this caller may not read the records it is derived from.** A Quantity
 *     Surveyor sees the Design gate and holds no `deliverable` grant, so the
 *     count and the owner are omitted, exactly as money is omitted rather than
 *     nulled (ADR-005).
 *
 * So there is no such thing as rendering a dash here. A missing count renders
 * nothing at all, and the item's label carries the meaning on its own.
 */
export interface GateEvidence {
  /** How many records hold it open. */
  readonly count?: number;
  /** The earliest date at issue, in the PROJECT's timezone. ISO. */
  readonly date?: string;
  /** Days from the project's today to `date`. **Negative means past.** */
  readonly days?: number;
  /** The one person who holds every record counted. Never a guess. */
  readonly owner?: string;
}

export interface GateItem {
  readonly key: string;
  readonly label: string;
  /** `not_checkable` — specified, but the records it needs do not exist yet. */
  readonly status: 'satisfied' | 'unsatisfied' | 'not_checkable';
  readonly waiting_for?: string;
  readonly evidence?: GateEvidence;
}

export interface StageDetail {
  readonly stage_key: string;
  readonly status: StageStatus;
  readonly sequence: number;
  readonly is_overridden: boolean;
  readonly override_reason: string | null;
  readonly started_at: string | null;
  /** Why the APP started it. Null when a person pressed Start. */
  readonly started_reason: string | null;
  readonly completed_at: string | null;
  readonly reopened_at: string | null;
  /** Why the app put this stage at risk, and when. Null unless `at_risk`. */
  readonly risk_signal: string | null;
  readonly risk_reason: string | null;
  readonly risk_since: string | null;
  readonly gate: { readonly items: readonly GateItem[]; readonly blocking: number };
  /** What THIS caller may move it to. Empty means no buttons. */
  readonly allowed_next: readonly StageStatus[];
}

export async function listStages(projectId: string): Promise<readonly StageDetail[]> {
  const body = await request<{ data: readonly StageDetail[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/stages`,
  );
  return body.data;
}

export async function moveStage(
  projectId: string,
  stageKey: string,
  input: { status: StageStatus; reason?: string },
): Promise<readonly StageDetail[]> {
  const body = await request<{ data: readonly StageDetail[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/stages/${encodeURIComponent(stageKey)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
  return body.data;
}

export interface Project {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly project_type: ProjectType;
  readonly status: string;
  readonly client_name: string | null;
  readonly start_date: string | null;
  readonly target_completion_date: string | null;
  /** Absent — not null — when the caller may not see money (ADR-005). */
  readonly contract_value?: string | null;
  readonly currency?: string;
}

/**
 * What the signed-in caller may do on this project.
 *
 * Decided by the server, not re-derived here: a second copy of the permission
 * rules in the browser is a copy that will eventually disagree with the one
 * that actually enforces them.
 */
export interface ProjectCapabilities {
  readonly add_member: boolean;
  readonly edit_member: boolean;
  readonly remove_member: boolean;
  readonly add_location: boolean;
  readonly edit_location: boolean;
  readonly remove_location: boolean;
  readonly write_diary: boolean;
  readonly edit_diary: boolean;
  /** Company Admin alone (`04`). Everyone else can only correct a day. */
  readonly delete_diary: boolean;
  readonly add_drawing: boolean;
  readonly edit_drawing: boolean;
  readonly remove_drawing: boolean;
  readonly export_drawing: boolean;
  readonly add_submittal: boolean;
  readonly edit_submittal: boolean;
  readonly remove_submittal: boolean;
  readonly export_submittal: boolean;
  readonly override_pulse: boolean;
  readonly edit_budget: boolean;
  readonly request_inspection: boolean;
  readonly conduct_inspection: boolean;
  readonly raise_ncr: boolean;
  readonly edit_ncr: boolean;
  readonly raise_snag: boolean;
  readonly edit_snag: boolean;
  readonly remove_snag: boolean;
  readonly export_snag: boolean;
  readonly add_material: boolean;
  readonly edit_material: boolean;
  readonly remove_material: boolean;
  readonly export_material: boolean;
  readonly add_milestone: boolean;
  readonly edit_milestone: boolean;
  readonly remove_milestone: boolean;
  readonly add_activity: boolean;
  readonly edit_activity: boolean;
  readonly remove_activity: boolean;
  /** Waiving needs approval rights, not edit rights — it opens the Site gate. */
  readonly waive_activity: boolean;
  readonly export_activity: boolean;
  readonly add_document: boolean;
  readonly edit_document: boolean;
  readonly remove_document: boolean;
  readonly export_document: boolean;
  readonly raise_variation: boolean;
  readonly edit_variation: boolean;
  readonly export_variation: boolean;
  readonly record_cost: boolean;
  readonly remove_cost: boolean;
  readonly export_cost: boolean;
  readonly export_task: boolean;
  /** `task: C`. A subcontractor holds `VE(assigned)` and was offered the button anyway. */
  readonly add_task: boolean;
  /**
   * The three reads the Overview is made of. A role holding none of them
   * gets "Your work on this job" instead of five refusals.
   */
  readonly view_pulse: boolean;
  readonly view_stages: boolean;
  readonly view_milestones: boolean;
  readonly raise_invoice: boolean;
  readonly edit_invoice: boolean;
  /** The client holds `handover: VA` — they SIGN, they do not write the checklist. */
  readonly write_handover: boolean;
  readonly edit_handover: boolean;
  readonly remove_handover: boolean;
  readonly sign_handover: boolean;
  /** RFQs belong to procurement; deliveries to whoever is at the gate. */
  readonly send_rfq: boolean;
  readonly record_delivery: boolean;
  /** Issuing is `report:approve` — sending the week out is a decision. */
  readonly generate_report: boolean;
  readonly edit_report: boolean;
  readonly issue_report: boolean;
  /**
   * `04` gives `project: E` to Company Admin, Director and PM.
   *
   * The server has always returned this; nothing in the browser read it, so
   * four Award gate items — client, contract value, start date, target date —
   * asked for something with no screen behind it. Optional because a bundle
   * older than the server would not have it.
   */
  readonly edit_project?: boolean;
  readonly archive_project?: boolean;
}

export interface ProjectDetail extends Project {
  readonly stages: readonly ProjectStage[];
  readonly member_count: number;
  /** Absent on the create response, which does not resolve capabilities. */
  readonly can?: ProjectCapabilities;
}

export interface CreateProjectInput {
  readonly code: string;
  readonly name: string;
  readonly project_type: ProjectType;
  readonly client_name?: string;
  readonly contract_value?: string;
  readonly start_date?: string;
  readonly target_completion_date?: string;
  readonly address?: string;
  /** A starting set — its milestones are copied in, anchored on start_date. */
  readonly template_id?: string;
}

export async function listProjects(): Promise<readonly Project[]> {
  const body = await request<{ data: readonly Project[] }>('/v1/projects');
  return body.data;
}

export function createProject(input: CreateProjectInput): Promise<ProjectDetail> {
  return request<ProjectDetail>('/v1/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getProject(id: string): Promise<ProjectDetail> {
  return request<ProjectDetail>(`/v1/projects/${encodeURIComponent(id)}`);
}

/**
 * Correct the project's own details (PR-06).
 *
 * The code, the currency, the timezone and the status are NOT here and are not
 * in the route's schema either — each one is a different project rather than an
 * edit, and the status belongs to the lifecycle.
 *
 * `contract_value` crosses as a decimal STRING. A JSON number is a float, and a
 * contract value that arrives as 1249999.9999999999 is the trap `07` §DI2 names.
 */
export interface UpdateProjectInput {
  readonly name?: string;
  readonly client_name?: string | null;
  readonly contract_value?: string | null;
  readonly start_date?: string | null;
  readonly target_completion_date?: string | null;
  readonly address?: string | null;
}

export function updateProject(id: string, input: UpdateProjectInput): Promise<ProjectDetail> {
  return request<ProjectDetail>(`/v1/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export interface ProjectMember {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  readonly project_role: string;
  readonly finance_view: boolean;
  readonly added_at: string;
}

export async function listProjectMembers(id: string): Promise<readonly ProjectMember[]> {
  const body = await request<{ data: readonly ProjectMember[] }>(
    `/v1/projects/${encodeURIComponent(id)}/members`,
  );
  return body.data;
}

export interface MemberCandidate {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  /** Their company role — the sensible default when adding them. */
  readonly company_role: string;
}

export async function listMemberCandidates(id: string): Promise<readonly MemberCandidate[]> {
  const body = await request<{ data: readonly MemberCandidate[] }>(
    `/v1/projects/${encodeURIComponent(id)}/member-candidates`,
  );
  return body.data;
}

/** Every team write answers with the whole new team, so the screen never guesses. */
async function teamWrite(path: string, init: RequestInit): Promise<readonly ProjectMember[]> {
  const body = await request<{ data: readonly ProjectMember[] }>(path, init);
  return body.data;
}

export function addProjectMember(
  projectId: string,
  input: { user_id: string; project_role: string; finance_view?: boolean },
): Promise<readonly ProjectMember[]> {
  return teamWrite(`/v1/projects/${encodeURIComponent(projectId)}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProjectMember(
  projectId: string,
  userId: string,
  input: { project_role?: string; finance_view?: boolean },
): Promise<readonly ProjectMember[]> {
  return teamWrite(
    `/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export function removeProjectMember(
  projectId: string,
  userId: string,
): Promise<readonly ProjectMember[]> {
  return teamWrite(
    `/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

// ------------------------------------------------------------------ milestones

export type MilestoneStatus = 'achieved' | 'late' | 'at_risk' | 'on_track' | 'not_set';

export interface Milestone {
  readonly id: string;
  readonly name: string;
  readonly baseline_date: string | null;
  readonly forecast_date: string | null;
  readonly actual_date: string | null;
  readonly is_critical: boolean;
  readonly sequence: number;
  /** forecast − baseline in days. Positive is late. */
  readonly variance_days: number | null;
  readonly status: MilestoneStatus;
}

async function milestoneCall(path: string, init?: RequestInit): Promise<readonly Milestone[]> {
  const body = await request<{ data: readonly Milestone[] }>(path, init ?? {});
  return body.data;
}

const msPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/milestones`;

export function listMilestones(projectId: string): Promise<readonly Milestone[]> {
  return milestoneCall(msPath(projectId));
}

export function createMilestone(
  projectId: string,
  input: { name: string; baseline_date?: string | null; is_critical?: boolean },
): Promise<readonly Milestone[]> {
  return milestoneCall(msPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

export function updateMilestone(
  projectId: string,
  milestoneId: string,
  input: {
    name?: string;
    forecast_date?: string | null;
    actual_date?: string | null;
    is_critical?: boolean;
  },
): Promise<readonly Milestone[]> {
  return milestoneCall(`${msPath(projectId)}/${encodeURIComponent(milestoneId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Changing what was promised. Its own call, because it is its own act. */
export function rebaselineMilestone(
  projectId: string,
  milestoneId: string,
  input: { baseline_date: string; reason: string },
): Promise<readonly Milestone[]> {
  return milestoneCall(`${msPath(projectId)}/${encodeURIComponent(milestoneId)}/rebaseline`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteMilestone(
  projectId: string,
  milestoneId: string,
): Promise<readonly Milestone[]> {
  return milestoneCall(`${msPath(projectId)}/${encodeURIComponent(milestoneId)}`, {
    method: 'DELETE',
  });
}

// -------------------------------------------------------------------- locations

export type LevelType = 'building' | 'level' | 'zone' | 'room';

export interface LocationNode {
  readonly id: string;
  readonly parent_id: string | null;
  readonly level_type: LevelType;
  readonly name: string;
  readonly code: string | null;
  readonly sequence: number;
  readonly path: string;
  readonly children: readonly LocationNode[];
}

/** Every location write answers with the whole tree, so the screen never guesses. */
async function locationWrite(path: string, init: RequestInit): Promise<readonly LocationNode[]> {
  const body = await request<{ data: readonly LocationNode[] }>(path, init);
  return body.data;
}

export async function listLocations(projectId: string): Promise<readonly LocationNode[]> {
  const body = await request<{ data: readonly LocationNode[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/locations`,
  );
  return body.data;
}

export function createLocation(
  projectId: string,
  input: { parent_id?: string | null; level_type: LevelType; name: string; code?: string },
): Promise<readonly LocationNode[]> {
  return locationWrite(`/v1/projects/${encodeURIComponent(projectId)}/locations`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface BulkLevelSpec {
  readonly level_type: LevelType;
  readonly name_pattern: string;
  readonly from: number;
  readonly to: number;
}

export function bulkCreateLocations(
  projectId: string,
  input: { parent_id?: string | null; levels: readonly BulkLevelSpec[] },
): Promise<readonly LocationNode[]> {
  return locationWrite(`/v1/projects/${encodeURIComponent(projectId)}/locations/bulk`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function renameLocation(
  projectId: string,
  locationId: string,
  input: { name?: string; code?: string | null },
): Promise<readonly LocationNode[]> {
  return locationWrite(
    `/v1/projects/${encodeURIComponent(projectId)}/locations/${encodeURIComponent(locationId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

export function deleteLocation(
  projectId: string,
  locationId: string,
): Promise<readonly LocationNode[]> {
  return locationWrite(
    `/v1/projects/${encodeURIComponent(projectId)}/locations/${encodeURIComponent(locationId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Exports

export interface ExportOutcome {
  /** Big enough that the company admins were told. */
  readonly notable: boolean;
  readonly told: readonly string[];
}

/**
 * Tell the server a register was just taken away.
 *
 * `09` §Threats: *"A user bulk-exports the whole project before resigning"* →
 * *"Every export is audited with row count and filters"*.
 *
 * Called BEFORE the file is saved, and it can refuse: the `X` right in `04` is
 * held by far fewer roles than `V`, and a subcontractor who may read the snag
 * register may not walk off with it. The screens hide the button, but the
 * button is not the rule — this is.
 */
export async function recordExport(
  projectId: string,
  resource: string,
  rows: number,
  filters: Readonly<Record<string, unknown>> = {},
): Promise<ExportOutcome> {
  const body = await request<{ data: ExportOutcome }>(
    `/v1/projects/${encodeURIComponent(projectId)}/exports`,
    { method: 'POST', body: JSON.stringify({ resource, rows, filters }) },
  );
  return body.data;
}
